import { Component, Input, OnDestroy, OnInit, Signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { IBiLink } from '@suvo-bi-lib';
import { CurrentTenantService } from '@suvo-bi-users';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CurrentTenantData } from '@suvo/frontend-framework/SUVO-USERS-CLIENT-LIB/projects/suvo-users-client-lib/src/lib/shared/interfaces/current-tenant-data.interface';
import { DynamicRouteService } from '../../../../../dynamic-routes';
import { ChatService } from '../../../chat/services/chat.service';
import { ORGANISATION_NAVIGATION_LINKS } from './org-navigation-links.constant';

@Component({
  standalone: false,
  selector: 'app-main-sidenav',
  templateUrl: './main-sidenav.component.html',
  styleUrls: ['./main-sidenav.component.scss'],
})
export class MainSidenavComponent implements OnInit, OnDestroy {
  private readonly unsubscribe$ = new Subject<void>();

  private readonly myAccountLink: IBiLink = {
    route: 'my-account',
    displayName: 'My Account',
    displayIcon: 'person',
    subLinks: [
      {
        route: ['my-account', 'profile'],
        displayName: 'Profile',
      },
      {
        route: ['my-account', 'preferences', 'favourites'],
        displayName: 'Favourites',
      },
      {
        route: ['my-account', 'subscription'],
        displayName: 'Subscription',
      },
    ],
  };

  private readonly helpLink: IBiLink = {
    route: ['/', 'help'],
    displayName: 'Help',
    displayIcon: 'help',
  };

  private dynamicLinks: IBiLink[] = [];
  private chatLink?: IBiLink;

  currentTenant: Signal<CurrentTenantData>;
  readonly loadingRows = Array.from({ length: 6 });
  loading = true;
  mainNavLinksLoaded = false;

  @Input() manageOrgsRoute: string[] | string = ['/', 'private', 'my-organisations'];

  links: IBiLink[] = [];
  organisationLinks = ORGANISATION_NAVIGATION_LINKS;

  constructor(
    private readonly router: Router,
    private readonly dynamicRoutes: DynamicRouteService,
    private readonly chatService: ChatService,
    private currentTenantService: CurrentTenantService,
  ) {
    this.currentTenant = this.currentTenantService.currentTenant;
  }

  viewingOrganisations = false;

  ngOnInit() {
    this.initNavLinks();

    this.router.events.pipe(takeUntil(this.unsubscribe$)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.checkRoutes();
      }
    });

    this.checkRoutes();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  checkRoutes() {
    this.viewingOrganisations = this.router.url.includes('/private/my-organisations');
  }

  handleLinkClick(selectedLink: IBiLink) {
    if (!selectedLink) return;

    const allLinks = this.viewingOrganisations ? this.organisationLinks : this.links;
    this.toggleDropdown(selectedLink, allLinks);
  }

  private toggleDropdown(selectedLink: IBiLink, allLinks: IBiLink[]) {
    const linkFound = this.findLink(selectedLink, allLinks);

    allLinks.forEach((link) => {
      if (link !== linkFound) {
        link.dropDownOpen = false;
      }
    });
  }

  private findLink(selectedLink: IBiLink, links: IBiLink[]): IBiLink | undefined {
    for (const link of links) {
      if (
        link === selectedLink ||
        (link.route && selectedLink.route && link.route === selectedLink.route)
      ) {
        link.dropDownOpen = true;
        return link;
      } else if (link.subLinks) {
        const found = this.findLink(selectedLink, link.subLinks);
        if (found) {
          link.dropDownOpen = true;
          return link;
        }
      }
    }
  }

  private async initNavLinks() {
    this.loading = true;
    this.mainNavLinksLoaded = false;
    this.links = [];

    void this.loadDynamicLinks();
    void this.loadDeferredLinks();
  }

  private async loadDynamicLinks() {
    try {
      this.dynamicLinks = await this.dynamicRoutes.getLinks({
        treeType: 'private',
        includeTree: true,
      });
    } catch {
      this.dynamicLinks = [];
    } finally {
      this.mainNavLinksLoaded = true;
      this.loading = false;
      this.rebuildLinks();
    }
  }

  private async loadDeferredLinks() {
    try {
      if (this.chatService.chatCapabilities() === undefined) {
        await this.chatService.loadCapabilities();
      }

      this.chatLink = this.chatService.chatEnabled()
        ? {
            route: 'chat',
            displayName: 'AI Chat',
            displayIcon: 'assistant',
          }
        : undefined;

      this.rebuildLinks();
    } catch {
      this.chatLink = undefined;
      this.rebuildLinks();
    }
  }

  private rebuildLinks() {
    this.links = [
      ...this.dynamicLinks,
      ...(this.chatLink ? [this.chatLink] : []),
      ...(this.mainNavLinksLoaded ? [this.myAccountLink] : []),
      this.helpLink,
    ];
  }
}
